import React, { useEffect, useCallback } from "react";
import register from "../assets/register.png";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginUserAction } from "../redux/actions/userActions";
import { LOGIN_USER_RESET } from "../redux/constants/userConstants";
import Loading from "./Loading";
import { toast } from "../utils/toast";
import QrButton from "./Button/QrButton";
import useFormValidation from "../hooks/useFormValidation";

function FirstLogin() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Extract file number passed from PhoneLookupScreen or other navigation states
  const prefilledFileNumber = typeof location.state === 'string'
    ? location.state
    : (location.state?.file_number || location.state?.system_id || "");

  const store = useSelector((state) => state.loginUser);
  const { loading, success, error } = store;

  // Form validation rules
  const validationRules = {
    file_number: {
      required: true,
      requiredMessage: "Phone number or file number is required",
    },
    password: {
      required: true,
      minLength: 6,
      requiredMessage: "Password is required",
      minLengthMessage: "Password must be at least 6 characters",
    },
  };

  // Initialize form with validation hook
  const {
    formData,
    errors: formErrors,
    isSubmitting,
    handleChange: handleInputChange,
    handleSubmit,
    setIsSubmitting,
    setFormField,
  } = useFormValidation(
    {
      file_number: prefilledFileNumber,
      password: "",
    },
    validationRules
  );

  useEffect(() => {
    dispatch({ type: LOGIN_USER_RESET });
  }, [dispatch]);

  // Auto-populate file number input if passed from navigation
  useEffect(() => {
    if (prefilledFileNumber) {
      setFormField("file_number", prefilledFileNumber);
    }
  }, [prefilledFileNumber, setFormField]);

  // Handle successful login
  useEffect(() => {
    if (success && !loading) {
      toast.success("Login successful! Welcome back.");
      setIsSubmitting(false);
      navigate("/predashboard");
    }
  }, [success, loading, navigate, setIsSubmitting]);

  // Handle login errors
  useEffect(() => {
    if (error && !loading) {
      setIsSubmitting(false);

      if (error.todo === "verify_otp") {
        navigate("/verify", { state: formData.file_number });
        toast.info("Please verify your account with the OTP sent to your phone");
      }

    }
  }, [error, loading, navigate, formData.file_number, setIsSubmitting]);

  // Handle form submission
  const onSubmit = useCallback(async (data) => {
    const patient_id = "";
    dispatch(loginUserAction(data.file_number, data.password, patient_id));
  }, [dispatch]);

  // Create the form submit handler
  const formSubmitHandler = handleSubmit(onSubmit);

  const handleQrLogin = useCallback(() => {
    navigate('/qr-login');
  }, [navigate]);

  const clearHospitalInfo = () => {
    localStorage.removeItem("hospital_name");
    localStorage.removeItem("hospital_phone");
    localStorage.removeItem("hospital_primary_color");
    localStorage.removeItem("hospital_secondary_color");
    localStorage.removeItem("hospital_opaque_color");
    localStorage.removeItem("hospital");
    toast.info("Hospital information cleared");
    window.location.reload();
  };

  const getHospitalName = () => {
    try {
      const stored = localStorage.getItem("hospital_name");
      if (stored) {
        try {
          return JSON.parse(stored);
        } catch {
          return stored;
        }
      }
    } catch (e) {
      // fallback
    }
    return "Misas";
  };

  const hospitalName = getHospitalName();

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex flex-col justify-center items-center px-4 py-6 sm:py-8">
      <div className="w-full max-w-md sm:max-w-lg lg:max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden">

        {/* Header Section with Background Image */}
        <div
          className="relative h-32 sm:h-48 lg:h-64 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${register})`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-primary100/80 to-primary100">
            <div className="absolute bottom-4 left-4 sm:bottom-6 sm:left-6">
              <h1 className="text-white text-xl sm:text-2xl lg:text-4xl font-bold">
                Welcome Back
              </h1>
              <p className="text-white/90 text-sm sm:text-lg lg:text-xl">
                to {hospitalName}
              </p>
            </div>
          </div>
        </div>

        {/* Login Form Section */}
        <div className="px-5 py-6 sm:px-6 sm:py-8">
          <div className="mb-5 sm:mb-6">
            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-800 mb-1 sm:mb-2">
              Sign In to Your Account
            </h2>
            <p className="text-gray-600 text-xs sm:text-sm lg:text-base">
              Enter your credentials to access your healthcare portal
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm font-medium">
                {error?.message}
              </p>
            </div>
          )}

          <form onSubmit={formSubmitHandler} className="space-y-6">
            {/* Phone/File Number Input */}
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                Phone Number or File Number
              </label>
              <input
                type="text"
                name="file_number"
                value={formData.file_number}
                placeholder="Enter your file number or phone"
                onChange={handleInputChange}
                className={`w-full px-3 py-2 sm:px-4 sm:py-3 text-sm sm:text-base border rounded-xl focus:ring-2 focus:ring-primary100 focus:border-primary100 transition-all duration-200 text-gray-800 placeholder-gray-400 ${formErrors.file_number ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                required
                disabled={isSubmitting}
              />
              {formErrors.file_number && (
                <p className="mt-1 text-sm text-red-600">{formErrors.file_number}</p>
              )}
            </div>

            {/* Password Input */}
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                Password
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                placeholder="Enter your password"
                onChange={handleInputChange}
                className={`w-full px-3 py-2 sm:px-4 sm:py-3 text-sm sm:text-base border rounded-xl focus:ring-2 focus:ring-primary100 focus:border-primary100 transition-all duration-200 text-gray-800 placeholder-gray-400 ${formErrors.password ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                required
                disabled={isSubmitting}
              />
              {formErrors.password && (
                <p className="mt-1 text-sm text-red-600">{formErrors.password}</p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="space-y-4">
              {loading || isSubmitting ? (
                <div className="flex justify-center py-4">
                  <Loading fullScreen={true} message="Signing In..." variant="medical" />
                </div>
              ) : (
                <>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-3 sm:py-4 bg-primary100 hover:bg-primary100/90 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-xl text-white text-base sm:text-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                  >
                    {isSubmitting ? 'Signing In...' : 'Sign In'}
                  </button>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-gray-500">or</span>
                    </div>
                  </div>

                  <QrButton
                    onClick={handleQrLogin}
                    text={'Login with QR Code'}
                    disabled={isSubmitting}
                  />
                </>
              )}
            </div>
          </form>

          {/* Reset Password Link */}
          <div className="mt-6 text-center">
            <Link
              to="/password-reset"
              className="text-primary100 font-semibold hover:text-primary100/80 transition-colors duration-200 text-sm"
            >
              Forgot your password?
            </Link>
          </div>

          {/* Footer Links */}
          <div className="mt-6 sm:mt-8 pt-5 sm:pt-6 border-t border-gray-100 text-center">
            <p className="text-gray-600 text-xs sm:text-sm">
              Don't have an account?{' '}
              <Link
                to="/register"
                className="text-primary100 font-semibold hover:text-primary100/80 transition-colors duration-200"
              >
                Create Account
              </Link>
            </p>
          </div>

          {/* Security Badge & Clear Info */}
          <div className="mt-5 sm:mt-6 flex flex-col items-center justify-center space-y-3">
            <div className="flex items-center space-x-2 text-[10px] sm:text-xs text-gray-500">
              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-500 rounded-full"></div>
              <span>Secure Login</span>
            </div>
            
            <button 
              type="button"
              onClick={clearHospitalInfo}
              className="text-[10px] sm:text-xs text-gray-400 hover:text-gray-600 underline underline-offset-2 transition-colors"
            >
              Clear device hospital info
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FirstLogin;
